"use client"

import { useMemo, useState } from "react"
import { motion } from "framer-motion"
import { LeadSourceBadge, getSourceMeta } from "@/components/kanban/lead-source-badge"
import { MetaBrand, InstagramBrand, FacebookBrand, ArrowUpRight, Megaphone } from "@/lib/icons"
import { normalizeStatus } from "@/lib/supabase"
import {
  getTopChannel,
  getMetaNetwork,
  getCampaignName,
  STAGE_BUCKETS,
  countStages,
} from "@/lib/attribution"
import { AttributionLeadsModal } from "./AttributionLeadsModal"

const IG_GRADIENT =
  "linear-gradient(135deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)"

const NETWORK_CONFIG = {
  instagram: { label: "Instagram", icon: InstagramBrand, bg: IG_GRADIENT, accent: "#E1306C" },
  facebook: { label: "Facebook", icon: FacebookBrand, bg: "#1877F2", accent: "#1877F2" },
  sin_red: { label: "Meta", icon: MetaBrand, bg: "#0866FF", accent: "#0866FF" },
}

const CHANNEL_CONFIG = {
  meta_ads: { accent: "#0866FF", caption: "Anuncios pagados en Instagram y Facebook" },
  landing: { accent: "#7a1b33", caption: "Botones de WhatsApp del sitio web" },
  whatsapp_directo: { accent: "#25D366", caption: "Recomendación y contacto directo" },
  manual: { accent: "#64748b", caption: "Registrados a mano desde el CRM" },
}

// Etapas que cuentan como "llegó a cita o más"
const ADVANCED_KEYS = new Set(["cita", "examen", "pago", "inscrito"])

const stats = (leadList) => {
  const buckets = countStages(leadList)
  let advanced = 0
  for (const k of ADVANCED_KEYS) advanced += buckets[k]
  return { buckets, advanced, inscritos: buckets.inscrito }
}

/**
 * Pestaña Campañas: la respuesta completa a "¿qué campaña me trae prospectos
 * y cómo van?". Cards de canal arriba + tabla unificada de campañas/orígenes,
 * cada fila clickeable hacia sus leads. Fuente: evidencia del webhook
 * (leads.source_metadata).
 */
export function CampaignsView({ leads = [] }) {
  const [modalGroup, setModalGroup] = useState(null)

  const { channels, rows } = useMemo(() => {
    const byChannel = new Map()
    for (const l of leads) {
      const ch = getTopChannel(l)
      if (ch === "sin_fuente") continue
      if (!byChannel.has(ch)) byChannel.set(ch, [])
      byChannel.get(ch).push(l)
    }
    const total = leads.length || 1

    const channels = Array.from(byChannel.entries())
      .map(([channel, list]) => ({
        channel,
        label: getSourceMeta(channel).label,
        config: CHANNEL_CONFIG[channel] || CHANNEL_CONFIG.manual,
        leads: list,
        share: (list.length / total) * 100,
        ...stats(list),
      }))
      .sort((a, b) => b.leads.length - a.leads.length)

    // Tabla plana: una fila por campaña / detalle de landing / entrada de WhatsApp
    const rows = []
    for (const ch of channels) {
      if (ch.channel === "meta_ads") {
        const map = new Map()
        for (const l of ch.leads) {
          const name = getCampaignName(l)
          if (!map.has(name)) map.set(name, { name, networks: {}, urls: {}, leads: [] })
          const g = map.get(name)
          g.leads.push(l)
          const net = getMetaNetwork(l) || "sin_red"
          g.networks[net] = (g.networks[net] || 0) + 1
          if (l.source_metadata?.ad_source_url && !g.urls[net]) g.urls[net] = l.source_metadata.ad_source_url
        }
        for (const g of map.values()) {
          rows.push({ channel: ch.channel, channelLabel: ch.label, accent: CHANNEL_CONFIG.meta_ads.accent, kind: "Campaña", ...g, ...stats(g.leads) })
        }
      } else if (ch.channel === "landing") {
        // Landing en una sola fila (pedido de Yair): el conteo general es lo
        // que importa; el detalle por botón no aporta a la decisión
        rows.push({ channel: ch.channel, channelLabel: ch.label, accent: CHANNEL_CONFIG.landing.accent, kind: "Sitio web", name: "Landing Page", leads: ch.leads, ...stats(ch.leads) })
      } else if (ch.channel === "whatsapp_directo") {
        // WhatsApp en una sola fila (pedido de Yair): el desglose por tipo de
        // entrada (Business, búsqueda, etc.) es lo mismo para la decisión
        rows.push({ channel: ch.channel, channelLabel: ch.label, accent: CHANNEL_CONFIG.whatsapp_directo.accent, kind: "Recomendación y contacto directo", name: "WhatsApp", leads: ch.leads, ...stats(ch.leads) })
      } else {
        rows.push({ channel: ch.channel, channelLabel: ch.label, accent: CHANNEL_CONFIG.manual.accent, kind: "Registro", name: "Alta manual en CRM", leads: ch.leads, ...stats(ch.leads) })
      }
    }
    rows.sort((a, b) => b.leads.length - a.leads.length)

    return { channels, rows }
  }, [leads])

  if (leads.length === 0) {
    return (
      <div className="rounded-2xl border border-slate-200/60 dark:border-slate-700/60 bg-white dark:bg-slate-900 p-10 text-center text-sm text-slate-400">
        Sin leads en el período seleccionado
      </div>
    )
  }

  return (
    <div className="space-y-5">
      {/* ── Cards de canal ── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {channels.map((ch, i) => (
          <motion.div
            key={ch.channel}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: i * 0.07 }}
            className="relative overflow-hidden rounded-2xl border border-slate-200/60 dark:border-slate-700/60 bg-white dark:bg-slate-900 p-4 shadow-sm"
          >
            <div className="pointer-events-none absolute inset-x-0 top-0 h-0.5" style={{ background: ch.config.accent }} />
            <div className="flex items-center justify-between gap-2">
              <LeadSourceBadge source={ch.channel} size={32} ringed={false} />
              <div className="text-right">
                <p className="text-2xl font-extrabold tabular-nums text-slate-900 dark:text-white leading-none">
                  {ch.leads.length}
                </p>
                <p className="text-[9px] uppercase tracking-wider text-slate-400 font-bold mt-0.5">
                  {ch.share.toFixed(0)}% del total
                </p>
              </div>
            </div>
            <p className="mt-2.5 text-[13px] font-extrabold text-slate-900 dark:text-slate-100 leading-tight">
              {ch.label}
            </p>
            <p className="text-[10px] text-slate-400 leading-tight">{ch.config.caption}</p>
            {/* Distribución de etapas del canal */}
            <div className="mt-3 h-1.5 rounded-full overflow-hidden flex bg-slate-100 dark:bg-slate-800">
              {STAGE_BUCKETS.map((b) => {
                const c = ch.buckets[b.key]
                if (!c) return null
                return (
                  <div
                    key={b.key}
                    className="h-full"
                    style={{ width: `${(c / ch.leads.length) * 100}%`, background: b.color }}
                    title={`${b.label}: ${c}`}
                  />
                )
              })}
            </div>
            <div className="mt-2 flex items-center justify-between text-[10px] font-bold tabular-nums">
              <span className="text-amber-600 dark:text-amber-400">{ch.advanced} a cita o más</span>
              <span className="text-emerald-600 dark:text-emerald-400">{ch.inscritos} inscritos</span>
            </div>
          </motion.div>
        ))}
      </div>

      {/* ── Tabla de campañas ── */}
      <div className="rounded-2xl border border-slate-200/60 dark:border-slate-700/60 bg-white dark:bg-slate-900 shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100 dark:border-slate-800 bg-gradient-to-r from-rose-50/70 via-white to-blue-50/40 dark:from-rose-950/20 dark:via-slate-900 dark:to-blue-950/15 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-rose-700 to-rose-950 text-white shadow-md shadow-rose-900/20">
              <Megaphone className="h-5 w-5" />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-widest font-bold text-slate-400 dark:text-slate-500 leading-none">
                Detalle por campaña
              </p>
              <h3 className="text-sm font-extrabold text-slate-900 dark:text-slate-100 leading-tight mt-0.5">
                ¿Qué me está trayendo prospectos?
              </h3>
            </div>
          </div>
          <p className="text-[10px] text-slate-400 hidden sm:block">clic en una fila = ver sus leads</p>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-slate-100 dark:border-slate-800">
                {["Origen", "Campaña / Detalle", "Leads", "A cita+", "Inscritos", "Anuncio"].map((h, i) => (
                  <th
                    key={h}
                    className={`px-4 py-2.5 text-[9px] uppercase tracking-widest font-bold text-slate-400 dark:text-slate-500 whitespace-nowrap ${i >= 2 && i <= 4 ? "text-right" : ""} ${i === 5 ? "text-center" : ""}`}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, idx) => (
                <tr
                  key={`${r.channel}-${r.name}`}
                  onClick={() =>
                    setModalGroup({
                      title: r.kind === "Campaña" ? `Campaña: ${r.name}` : r.name,
                      subtitle: r.channelLabel,
                      accent: r.accent,
                      leads: r.leads,
                    })
                  }
                  className="border-b border-slate-50 dark:border-slate-800/60 last:border-0 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors group animate-in fade-in-0"
                  style={{ animationDelay: `${idx * 35}ms`, animationFillMode: "both" }}
                >
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-2">
                      <LeadSourceBadge source={r.channel} size={22} ringed={false} />
                      <span className="text-[11px] font-bold text-slate-500 dark:text-slate-400 hidden md:inline">
                        {r.channelLabel}
                      </span>
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-[12.5px] font-bold text-slate-800 dark:text-slate-100 truncate max-w-[260px]">
                        {r.name}
                      </span>
                      {r.networks && (
                        <span className="flex items-center gap-1 flex-shrink-0">
                          {Object.entries(r.networks)
                            .sort((a, b) => b[1] - a[1])
                            .map(([n, c]) => {
                              const cfg = NETWORK_CONFIG[n] || NETWORK_CONFIG.sin_red
                              const NIcon = cfg.icon
                              return (
                                <span
                                  key={n}
                                  className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[9px] font-bold text-white tabular-nums"
                                  style={{ background: cfg.bg }}
                                  title={`${cfg.label}: ${c} leads`}
                                >
                                  <NIcon className="h-2.5 w-2.5" /> {c}
                                </span>
                              )
                            })}
                        </span>
                      )}
                    </div>
                    <p className="text-[9.5px] text-slate-400 mt-0.5">{r.kind}</p>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className="text-[14px] font-extrabold tabular-nums text-slate-900 dark:text-white">
                      {r.leads.length}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className={`text-[12px] font-bold tabular-nums ${r.advanced > 0 ? "text-amber-600 dark:text-amber-400" : "text-slate-300 dark:text-slate-600"}`}>
                      {r.advanced}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className={`text-[12px] font-bold tabular-nums ${r.inscritos > 0 ? "text-emerald-600 dark:text-emerald-400" : "text-slate-300 dark:text-slate-600"}`}>
                      {r.inscritos}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    {r.urls && Object.keys(r.urls).length > 0 ? (
                      <span className="inline-flex items-center gap-1">
                        {Object.entries(r.urls).map(([n, url]) => {
                          const cfg = NETWORK_CONFIG[n] || NETWORK_CONFIG.sin_red
                          const NIcon = cfg.icon
                          return (
                            <a
                              key={n}
                              href={url}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[9px] font-bold text-white shadow-sm hover:scale-105 hover:shadow-md transition-all"
                              style={{ background: cfg.bg }}
                              title={`Ver el anuncio publicado en ${cfg.label}`}
                            >
                              <NIcon className="h-3 w-3" />
                              {cfg.label === "Instagram" ? "IG" : cfg.label === "Facebook" ? "FB" : "Meta"}
                              <ArrowUpRight className="h-2.5 w-2.5" />
                            </a>
                          )
                        })}
                      </span>
                    ) : (
                      <span className="text-slate-200 dark:text-slate-700">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Leyenda de etapas */}
        <div className="px-4 py-2 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50 flex items-center justify-center gap-2.5 flex-wrap">
          {STAGE_BUCKETS.map((b) => (
            <span key={b.key} className="inline-flex items-center gap-1 text-[9px] font-bold text-slate-400 dark:text-slate-500">
              <span className="h-1.5 w-1.5 rounded-full" style={{ background: b.color }} />
              {b.label}
            </span>
          ))}
        </div>
      </div>

      {modalGroup && <AttributionLeadsModal group={modalGroup} onClose={() => setModalGroup(null)} />}
    </div>
  )
}
